import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { History, Wand2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { workspaceAPI } from '../../../services/api';
import { fmtMDHM, asArray } from '../../service/sopShared';
import { S, btnPrimary, btnGhost, btnLink, field, QUICK_FIELDS, quickLabel, NotDeployed, is404 } from './wsShared';

// 匹配速记卡 — the 17 fields the SOP asks for, so another teacher can read the
// matching direction in 30 seconds. Changing a field that already had a value
// needs a 修改原因; every save lands in the history with a one-line summary.
// The backend fills EMPTY fields by itself once 画像 / 爱的语言 is done (history row by 系统).
// 「从画像 / 爱的语言 / 档案带入」 re-pulls 档案, 人生合伙人画像 and the 爱的语言 test (GET /quick-card/suggest)
// into the draft for anything still empty; the teacher checks and saves.
//
// Props: profileId, isZh, canSeeDivorce (member's coach / 主管), hints
// ({ member_no, age, city } placeholders from the profile), onChanged.

export default function QuickCardPanel({ profileId, isZh = true, canSeeDivorce = true, hints = {}, onChanged }) {
  const t = (zh, en) => (isZh ? zh : en);
  const [data, setData] = useState(null);       // { fields, updated_at, updated_by_name, history }
  const [missing, setMissing] = useState(false);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [brought, setBrought] = useState({});   // key -> source of a value brought in (this edit)
  const [sugBusy, setSugBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await workspaceAPI.getQuickCard(profileId);
      const d = r.data || {};
      setData({ fields: d.fields && typeof d.fields === 'object' ? d.fields : {}, updated_at: d.updated_at, updated_by_name: d.updated_by_name, history: asArray(d.history), can_see_private: d.can_see_private });
      setMissing(false);
    } catch (e) {
      if (is404(e)) setMissing(true);
      setData({ fields: {}, history: [] });
    }
  }, [profileId]);
  useEffect(() => { load(); }, [load]);

  const fields = (data && data.fields) || {};
  const seePrivate = data && data.can_see_private != null ? !!data.can_see_private : canSeeDivorce;
  const shownFields = QUICK_FIELDS.filter(([k]) => k !== 'divorce_reason' || seePrivate);
  const filled = shownFields.filter(([k]) => String(fields[k] || '').trim()).length;

  const changed = useMemo(() => QUICK_FIELDS.filter(([k]) => String(draft[k] ?? '').trim() !== String(fields[k] ?? '').trim()), [draft, fields]);
  const [forceReason, setForceReason] = useState(false);
  const needsReason = forceReason || changed.some(([k]) => String(fields[k] ?? '').trim());

  if (missing) return <NotDeployed isZh={isZh} what={t('匹配速记卡', 'The quick card')} />;
  if (!data) return <div style={{ color: S.mute, fontSize: 13 }}>{t('加载中…', 'Loading…')}</div>;

  const startEdit = () => {
    if (!filled) { bringIn(); return; }
    setDraft({ ...fields }); setBrought({}); setReason(''); setForceReason(false); setEditing(true);
  };
  const stopEdit = () => { setEditing(false); setBrought({}); };
  // Fill the empty fields of the draft (or of the saved card when not editing yet); never overwrite.
  const bringIn = async () => {
    setSugBusy(true);
    try {
      const r = await workspaceAPI.suggestQuickCard(profileId);
      const sug = (r.data && r.data.fields) || {};
      const src = (r.data && r.data.sources) || {};
      const base = editing ? { ...draft } : { ...fields };
      const added = {};
      shownFields.forEach(([k]) => {
        if (k === 'divorce_reason' || String(base[k] ?? '').trim() || !String(sug[k] ?? '').trim()) return;
        base[k] = sug[k]; added[k] = src[k] || '档案';
      });
      if (!editing) { setReason(''); setForceReason(false); }
      setDraft(base); setBrought((b) => ({ ...(editing ? b : {}), ...added })); setEditing(true);
      const n = Object.keys(added).length;
      if (n) toast.success(t(`已带入 ${n} 项，请核对后点「保存」`, `Brought in ${n} fields — check and save`));
      else toast.info(t('档案、画像和爱的语言里没有可以补充的内容', 'Nothing more to bring in'));
    } catch (e) {
      toast.error(is404(e) ? t('后台还没有更新，暂时不能带入', 'Not available yet') : (e?.response?.data?.error || t('带入失败', 'Could not bring in')));
      if (!editing && !filled) { setDraft({ ...fields }); setBrought({}); setReason(''); setForceReason(false); setEditing(true); }
    } finally { setSugBusy(false); }
  };
  const save = async () => {
    if (!changed.length) { setEditing(false); return; }
    if (needsReason && !reason.trim()) { toast.error(t('改了已有的内容，请写修改原因', 'Say why you changed it')); return; }
    setBusy(true);
    try {
      const next = {};
      // Only what changed (the API does a partial update; '' clears a field).
      changed.forEach(([k]) => { next[k] = String(draft[k] ?? '').trim(); });
      await workspaceAPI.saveQuickCard(profileId, { fields: next, reason: reason.trim() || undefined });
      toast.success(t('速记卡已保存', 'Saved'));
      stopEdit();
      await load();
      onChanged && onChanged();
    } catch (e) {
      const need = e?.response?.data?.fields_need_reason;
      if (Array.isArray(need) && need.length) {
        setForceReason(true);
        toast.error(t(`改了「${need.map(quickLabel).join('、')}」，请写修改原因`, 'Say why you changed it'));
        return;
      }
      toast.error(e?.response?.data?.error || t('保存失败', 'Could not save'));
    } finally { setBusy(false); }
  };

  const hist = (data.history || []).slice().sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const summaryOf = (h) => h.summary || [
    asArray(h.changes).map((c) => `${quickLabel(c.field)}：${c.from || '空'} → ${c.to || '空'}`).join('；'),
    h.reason ? `原因：${h.reason}` : '',
  ].filter(Boolean).join('；');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: filled >= 8 ? S.ok : S.warn, fontWeight: 500 }}>
          {t(`已填 ${filled} / ${shownFields.length} 项`, `${filled} / ${shownFields.length} filled`)}{filled < 8 ? t('（填满 8 项以上，服务进度里的「速记卡」自动完成）', ' (8+ completes the step)') : ''}
        </span>
        {data.updated_at && <span style={{ fontSize: 12, color: S.mute }}>{t(`最后修改 ${fmtMDHM(data.updated_at)}${data.updated_by_name ? ` · ${data.updated_by_name}` : ''}`, `Updated ${fmtMDHM(data.updated_at)}`)}</span>}
        <span style={{ flex: 1 }} />
        {filled < shownFields.length && (
          <button type="button" disabled={sugBusy} onClick={bringIn} style={{ ...btnGhost, display: 'inline-flex', alignItems: 'center', gap: 5 }}
            title={t('把档案、人生合伙人画像和爱的语言测评里已有的内容填进空着的栏位，不会覆盖已填的内容', 'Fill empty fields from the profile, portrait and love-language test')}>
            <Wand2 size={13} />{sugBusy ? t('带入中…', 'Loading…') : t('从画像 / 爱的语言 / 档案带入', 'Bring in from profile')}
          </button>
        )}
        {!editing && <button type="button" disabled={sugBusy} onClick={startEdit} style={btnPrimary(sugBusy)}>{filled ? t('修改速记卡', 'Edit') : t('填写速记卡', 'Fill in')}</button>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px 16px' }}>
        {shownFields.map(([k, label, long]) => (
          <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4, gridColumn: long && editing ? 'span 1' : undefined }}>
            <span style={{ fontSize: 12, color: S.sub, fontWeight: 500 }}>
              {label}{k === 'divorce_reason' ? <span style={{ color: S.mute, fontWeight: 400 }}>{t('（仅主服务老师和主管可见）', ' (coach + supervisors only)')}</span> : null}
              {editing && brought[k] && String(draft[k] ?? '') !== '' ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: S.ok }}>{t(`已从${brought[k]}带入`, 'brought in')}</span> : null}
            </span>
            {editing ? (
              long
                ? <textarea rows={2} value={draft[k] || ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} style={{ ...field, resize: 'vertical' }} placeholder={hints[k] || ''} />
                : <input value={draft[k] || ''} onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} style={field} placeholder={hints[k] || ''} />
            ) : (
              <span style={{ fontSize: 13.5, color: fields[k] ? S.ink : S.mute, lineHeight: 1.6, overflowWrap: 'anywhere', minHeight: 22 }}>{fields[k] || '—'}</span>
            )}
          </label>
        ))}
      </div>

      {editing && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 2, background: S.soft, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {needsReason && (
            <label style={{ fontSize: 12.5, color: S.sub, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {t('修改原因（必填，资料更新要写明原因）', 'Why the change (required)')}
              <input value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} style={field}
                placeholder={t('例如：会员同意把年龄范围放宽到 38 岁', 'e.g. member agreed to widen the age range')} />
            </label>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: S.mute, marginRight: 'auto' }}>{changed.length ? t(`改了 ${changed.length} 项`, `${changed.length} changed`) : t('还没有改动', 'No changes')}</span>
            <button type="button" onClick={stopEdit} style={btnGhost}>{t('取消', 'Cancel')}</button>
            <button type="button" disabled={busy} onClick={save} style={btnPrimary(busy)}>{busy ? t('保存中…', 'Saving…') : t('保存', 'Save')}</button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <button type="button" onClick={() => setShowHist((v) => !v)} style={{ ...btnLink, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <History size={13} />{t(`修改记录（${hist.length}）`, `History (${hist.length})`)}
        </button>
        {showHist && (
          hist.length ? (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
              {hist.map((h, i) => (
                <div key={h.id || i} style={{ padding: '8px 0', borderBottom: `1px solid ${S.line}`, fontSize: 13 }}>
                  <div style={{ color: S.ink, lineHeight: 1.6, overflowWrap: 'anywhere' }}>{summaryOf(h)}</div>
                  <div style={{ fontSize: 12, color: S.mute }}>{[h.changed_by_name || (h.changed_by ? '' : t('系统', 'System')), fmtMDHM(h.created_at)].filter(Boolean).join(' · ')}</div>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 12.5, color: S.mute, marginTop: 6 }}>{t('还没有修改记录', 'No changes yet')}</div>
        )}
      </div>
    </div>
  );
}
